import { KeyboardEvent, SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import {
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from '@components/ui/drawer';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { formatTenantTime } from '@utils/i18n';
import { cn } from '@utils/cn';
import { SchedulerConflictsProps } from './types';

export const SchedulerConflicts = ({
  name,
  title,
  conflicts,
  iconClassName,
}: SchedulerConflictsProps) => {
  const { t } = useTranslation('app');
  const isTouch = useMediaQuery('(hover: none)');
  const [open, setOpen] = useState(false);

  const icon = (
    <SvgConsumer id="warning" className={cn('shrink-0 text-warning-500', iconClassName)} />
  );

  if (!isTouch)
    return (
      <AppTooltip
        className="max-w-none"
        context={
          <div className="flex w-fit flex-col whitespace-nowrap">
            <strong>{title}</strong>
            {conflicts.map((el) => (
              <span key={el.scheduleId}>
                {[
                  el.projectIdentifier,
                  el.name,
                  formatTenantTime(el.startsAt),
                  formatTenantTime(el.endsAt),
                ]
                  .filter(Boolean)
                  .join(' - ')}
              </span>
            ))}
          </div>
        }>
        <span className="shrink-0">{icon}</span>
      </AppTooltip>
    );

  const openDrawer = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-label={title}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openDrawer}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') openDrawer(e);
        }}
        className="-m-2 flex h-9 w-9 shrink-0 items-center justify-center">
        {icon}
      </span>
      <DrawerRoot open={open} onOpenChange={setOpen} direction="bottom">
        <DrawerContent
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          overlayClassName="z-[70] bg-[rgba(24,28,33,.45)]"
          className="z-[70] max-h-[75dvh] rounded-t-2xl border-0 pb-[calc(10px+env(safe-area-inset-bottom,0px))] shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
          <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-neutral-200" />
          <DrawerHeader className="flex shrink-0 flex-row items-center justify-between border-b py-1.5 pl-[18px] pr-2">
            <DrawerTitle className="m-0 flex min-w-0 items-center gap-2 text-base font-bold text-neutral-800">
              <SvgConsumer id="warning" className="shrink-0 text-warning-500" />
              <span className="truncate">{name}</span>
            </DrawerTitle>
            <DrawerClose asChild>
              <button
                type="button"
                aria-label={t('actions.close')}
                className="flex h-11 w-11 items-center justify-center rounded-md text-xl text-neutral-600 hover:bg-neutral-50">
                <SvgConsumer id="close" />
              </button>
            </DrawerClose>
          </DrawerHeader>
          <div className="flex min-h-0 flex-col gap-2 overflow-y-auto overscroll-contain p-4">
            <span className="text-sm font-semibold text-neutral-500">{title}</span>
            {conflicts.map((el) => (
              <div
                key={el.scheduleId}
                className="flex flex-col gap-0.5 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2">
                <span className="text-sm font-semibold text-neutral-800">
                  {[el.projectIdentifier, el.name].filter(Boolean).join(' - ')}
                </span>
                <span className="text-xs text-neutral-600">
                  {[formatTenantTime(el.startsAt), formatTenantTime(el.endsAt)]
                    .filter(Boolean)
                    .join(' - ')}
                </span>
              </div>
            ))}
          </div>
        </DrawerContent>
      </DrawerRoot>
    </>
  );
};
